'use client';

import React from 'react';
import Link from 'next/link';

interface ResourceSection {
  title: string;
  description: string;
  icon: React.ReactNode;
  link?: string;
  label: string;
}

export default function ResourcesContent() {
  const resourceSections: ResourceSection[] = [
    {
      title: 'Service and Technical Manuals',
      description: 'Find the documentation you need for proper operation, maintenance, and troubleshooting across our product lines.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      link: '/service-manuals',
      label: 'Manuals'
    },
    {
      title: 'Schematics and Technical Drawings',
      description: 'Access clear, accurate electrical diagrams to ensure proper installation and service.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      link: '/wiring-schematics',
      label: 'Schematics'
    },
    {
      title: 'State Specific Regulations',
      description: 'Navigate state-by-state elevator codes, safety standards, and legislative updates with our interactive map. Track proposed changes and access compliance documentation for all 50 states.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      link: '/state-specific-regulations',
      label: 'Regulations'
    },
    {
      title: 'Project Specification Form',
      description: 'Submit detailed project specifications including measurements, floor plans, and equipment requirements with our interactive drawing tool.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      link: '/project-specification-form',
      label: 'Specifications'
    },
    {
      title: 'Service & Support Requests',
      description: 'Need assistance? Submit a request and our team will help identify issues, locate the correct parts, or guide you through the next steps.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      link: '/service-support-request-form',
      label: 'Support'
    },
    {
      title: 'Installation & Training Videos',
      description: 'Step-by-step visual guides to help you install, configure, and service your equipment with confidence.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      link: '/installation-videos',
      label: 'Videos'
    },
    {
      title: 'Industry Information',
      description: 'Stay informed about elevator industry standards, regulations, safety guidelines, and best practices for professionals.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      link: '/industry-information',
      label: 'Industry'
    },
    {
      title: 'Elevator Terminology',
      description: 'Comprehensive glossary of professional elevator terms and definitions for technicians, engineers, and industry professionals.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      link: '/elevator-terminology',
      label: 'Terminology'
    },
    {
      title: 'FAQ',
      description: 'Quick answers to common questions about parts, compatibility, orders, and support.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      link: '/faq',
      label: 'FAQ'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section — black with red geometric accents */}
      <section className="relative bg-black py-20 lg:py-28 overflow-hidden">
        {/* Red geometric accent — left border bar */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-red-600" />
        {/* Red geometric accent — diagonal corner */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-red-600 opacity-10" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            {/* Category label */}
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-0.5 bg-red-600" />
              <span className="text-red-500 text-xs font-bold uppercase tracking-widest">Resource Hub</span>
            </div>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold mb-6 text-white leading-tight">
              Your Complete<br /><span className="text-red-600">Resource Hub</span>
            </h1>
            <p className="font-body text-lg lg:text-xl text-gray-300 leading-relaxed">
              As your parts supplier, we know having the right information is just as important as having the right components. 
              Quick access to service manuals, wiring diagrams, training materials, and support — everything you need to keep your equipment running smoothly and your install on time.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resourceSections.map((resource, index) => (
              <Link
                key={index}
                href={resource.link || '#'}
                className="bg-white border-2 border-black overflow-hidden hover:shadow-xl transition-all duration-300 block group"
              >
                {/* Red top accent bar */}
                <div className="h-1.5 bg-red-600 w-full" />

                <div className="p-6">
                  {/* Icon + label row */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-red-600">
                      {resource.icon}
                    </div>
                    <span className="text-xs font-bold text-red-600 uppercase tracking-widest border border-red-600 px-2 py-0.5">
                      {resource.label}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-heading text-xl font-bold text-black mb-3 leading-tight">
                    {resource.title}
                  </h3>

                  {/* Description */}
                  <p className="font-body text-gray-600 text-sm leading-relaxed mb-6">
                    {resource.description}
                  </p>

                  {/* CTA row */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-sm font-bold text-black uppercase tracking-wide">Learn More</span>
                    <div className="w-8 h-8 bg-red-600 flex items-center justify-center group-hover:bg-red-700 transition-colors">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section — black with red accent */}
      <section className="relative bg-black py-16 lg:py-24 overflow-hidden">
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-600 opacity-10" style={{ clipPath: 'polygon(0 100%, 100% 100%, 0 0)' }} />
        <div className="absolute top-0 right-0 w-2 h-full bg-red-600" />
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center space-y-6 relative z-10">
          <div className="inline-flex items-center gap-2 justify-center mb-2">
            <div className="w-8 h-0.5 bg-red-600" />
            <span className="text-red-500 text-xs font-bold uppercase tracking-widest">Expert Support</span>
            <div className="w-8 h-0.5 bg-red-600" />
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white">
            Cannot Find What You Need?
          </h2>
          <p className="font-body text-lg text-gray-300 leading-relaxed">
            Our support team is here to help you locate the right resources for your project.
          </p>
          <Link
            href="/service-support-request-form"
            className="inline-block px-8 py-4 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:border-red-700 hover:-translate-y-1 hover:shadow-lg"
          >
            Contact Support
          </Link>
        </div>
      </section>
    </div>
  );
}